export const FILE_TYPES = [
  { value: 'fa_new_planning_file', text: 'New Planning File', validated: true },
  { value: 'fa_import_file', text: 'Fixed Assets Import', validated: true },
  { value: 'fa_support_file', text: 'Fixed Assets Support', validated: true },
  { value: 'fa_rates_file', text: 'Planning Rates (FX)', validated: true },
  { value: 'loans_planning_file', text: 'Loans Planning', validated: true },
  { value: 'archive_planning_file', text: 'Archive Planning', validated: true },
  { value: 'risk_planning_file', text: 'Risk & Funding', validated: true },
  { value: 'vendor_planning_file', text: 'Vendor Planning', validated: false },
];

const VALIDATED_FILE_TYPES = FILE_TYPES.filter(ft => ft.validated).map(ft => ft.value);

// --- Pass 1: Filename-based detection (instant, client-side) ---

const FILENAME_PATTERNS = [
  { type: 'fa_new_planning_file', patterns: [/new.?planning/i, /capex.?sales/i] },
  { type: 'fa_import_file', patterns: [/fa_import/i, /fixed.?asset.*import/i, /assets?.?database/i] },
  { type: 'fa_support_file', patterns: [/fa_support/i, /fixed.?asset.*support/i] },
  { type: 'fa_rates_file', patterns: [/fa_rates/i, /planning.?rates/i, /fx.?rates/i] },
  { type: 'loans_planning_file', patterns: [/loans?.?planning/i, /contracts?.?planning/i] },
  { type: 'archive_planning_file', patterns: [/archive/i] },
  { type: 'risk_planning_file', patterns: [/risk/i] },
  { type: 'vendor_planning_file', patterns: [/vendor/i] },
];

/**
 * Detect file type from filename using regex patterns.
 * @param {string} filename
 * @returns {{ type: string, confidence: 'high'|'low' } | null}
 */
export function detectByFilename(filename) {
  const name = (filename || '').toLowerCase();
  let result = null;
  FILENAME_PATTERNS.some(entry => entry.patterns.some((pattern) => {
    if (pattern.test(name)) {
      result = { type: entry.type, confidence: 'high' };
      return true;
    }
    return false;
  }));
  return result;
}

// --- Pass 2: Sheet-name-based detection (after backend inspect call) ---

const SHEET_SIGNATURES = [
  {
    type: 'fa_new_planning_file',
    required: ['CAPEX', 'SALES'],
    optional: [],
  },
  {
    type: 'fa_import_file',
    required: [],
    oneOf: ['OL_ASSETS_DATABASE', 'ASSETS_DATABASE_EXCL_MOVABLE'],
    optional: [],
  },
  {
    type: 'fa_rates_file',
    required: ['Planning rates'],
    optional: [],
  },
  {
    type: 'loans_planning_file',
    required: ['new_contracts', 'active_contracts'],
    optional: [],
  },
  {
    type: 'archive_planning_file',
    required: ['PL', 'PL_ECO', 'BS_ECO', 'BS'],
    optional: [],
  },
  {
    type: 'risk_planning_file',
    required: ['RISK', 'EXISTING FUNDING'],
    optional: ['mapping'],
  },
  {
    type: 'fa_support_file',
    required: ['Sheet1'],
    optional: [],
    lowConfidence: true,
  },
];

const REQUIRED_SCORE = 10;
const OPTIONAL_SCORE = 3;
const MIN_THRESHOLD = 10;

function sheetMatch(normalizedSheets, sheetName) {
  return normalizedSheets.some(s => s.toLowerCase() === sheetName.toLowerCase());
}

/**
 * Detect file type from sheet names.
 * @param {string[]} sheetNames - list of sheet names from the workbook
 * @returns {{ type: string, confidence: 'high'|'low' } | null}
 */
export function detectBySheetNames(sheetNames) {
  if (!sheetNames || !sheetNames.length) return null;

  const normalizedSheets = sheetNames.map(s => s.trim());
  let bestMatch = null;
  let bestScore = 0;

  SHEET_SIGNATURES.forEach((sig) => {
    let score = 0;
    let allRequiredFound = true;

    // Check required sheets
    (sig.required || []).forEach((req) => {
      if (sheetMatch(normalizedSheets, req)) {
        score += REQUIRED_SCORE;
      } else {
        allRequiredFound = false;
      }
    });

    // Check oneOf sheets (at least one must match)
    if (sig.oneOf && sig.oneOf.length) {
      const hasOneOf = sig.oneOf.some(name => sheetMatch(normalizedSheets, name));
      if (hasOneOf) {
        score += REQUIRED_SCORE;
      } else {
        allRequiredFound = false;
      }
    }

    // Check optional sheets
    (sig.optional || []).forEach((opt) => {
      if (sheetMatch(normalizedSheets, opt)) {
        score += OPTIONAL_SCORE;
      }
    });

    if (!allRequiredFound || score < MIN_THRESHOLD) return;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = {
        type: sig.type,
        confidence: sig.lowConfidence ? 'low' : 'high',
      };
    }
  });

  return bestMatch;
}

/**
 * Check if a file type is in the validated set (can be sent to /validate/jobs).
 * @param {string} fileType
 * @returns {boolean}
 */
export function isValidatedType(fileType) {
  return VALIDATED_FILE_TYPES.includes(fileType);
}
